import React, { useState, useRef, useEffect } from 'react';
import { fetchOpenAIText } from '../../utils/openaiClient';
import './WordTranslationExercise.css';

function shuffle(array) {
  const arr = array.slice();
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

const WordTranslationExercise = ({
  pairs: initialPairs, title, targetLanguage, onClose,
  updateAttributes, node, editor, getPos,
  isStudent = false,
  myState = {}, otherState = {}, updateState = () => {}
}) => {
  const [mode, setMode] = useState('matching'); // 'matching' | 'glossary'
  const [selectedWord, setSelectedWord] = useState(null);
  const [matchedPairs, setMatchedPairs] = useState([]);
  const [errorPair, setErrorPair] = useState(null);
  const [flashPair, setFlashPair] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [showResult, setShowResult] = useState(false);
  const [wrongAttempts, setWrongAttempts] = useState(0);
  const [firstTrySuccess, setFirstTrySuccess] = useState(0);
  const [resultsVisible, setResultsVisible] = useState(false);
  const [pairs, setPairs] = useState(initialPairs);
  const [shuffledTranslations, setShuffledTranslations] = useState(() =>
    shuffle(initialPairs.map(p => p.translation))
  );
  const [isEditing, setIsEditing] = useState(false);
  const [editableTitle, setEditableTitle] = useState(title);
  const [editableTargetLanguage, setEditableTargetLanguage] = useState(targetLanguage);
  const addBtnRef = useRef(null);
  const genTimersRef = useRef({});
  const lastGeneratedRef = useRef({});
  const [isGenerating, setIsGenerating] = useState({});

  const syncedRef = useRef(false);
  const prevResetAtRef = useRef(null);
  const shuffleInitializedRef = useRef(false);
  const attemptsRef = useRef({});

  useEffect(() => {
    const timers = genTimersRef.current;
    return () => { Object.values(timers).forEach(id => clearTimeout(id)); };
  }, []);

  // --- Shuffle init: broadcast own order so both sides sync ---
  useEffect(() => {
    if (pairs.length === 0 || shuffleInitializedRef.current) return;
    shuffleInitializedRef.current = true;
    const shuffled = shuffle(pairs.map(p => p.translation));
    setShuffledTranslations(shuffled);
    updateState({ shuffledTranslations: shuffled });
  }, [pairs]); // eslint-disable-line

  // --- Restore state on mount (own or other side's) ---
  useEffect(() => {
    if (syncedRef.current) return;
    const ownPairs = Array.isArray(myState.matchedPairs) && myState.matchedPairs.length > 0 ? myState.matchedPairs : null;
    const otherPairs = Array.isArray(otherState.matchedPairs) && otherState.matchedPairs.length > 0 ? otherState.matchedPairs : null;
    const pairsToRestore = ownPairs || otherPairs;
    const resultsToRestore = myState.resultsVisible || otherState.resultsVisible;
    if (!pairsToRestore && !resultsToRestore) return;
    syncedRef.current = true;
    if (pairsToRestore) setMatchedPairs(pairsToRestore);
    if (resultsToRestore || (pairsToRestore && pairsToRestore.length === initialPairs.length)) setResultsVisible(true);
    const wa = typeof myState.wrongAttempts === 'number' ? myState.wrongAttempts : otherState.wrongAttempts;
    const fts = typeof myState.firstTrySuccess === 'number' ? myState.firstTrySuccess : otherState.firstTrySuccess;
    if (typeof wa === 'number') setWrongAttempts(wa);
    if (typeof fts === 'number') setFirstTrySuccess(fts);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Real-time: adopt other side's shuffle order ---
  useEffect(() => {
    if (!Array.isArray(otherState.shuffledTranslations) || otherState.shuffledTranslations.length === 0) return;
    setShuffledTranslations(otherState.shuffledTranslations);
  }, [otherState.shuffledTranslations]); // eslint-disable-line

  // --- Real-time: mirror other side's matched pairs ---
  useEffect(() => {
    if (!Array.isArray(otherState.matchedPairs)) return;
    setMatchedPairs(otherState.matchedPairs);
  }, [otherState.matchedPairs]); // eslint-disable-line

  // --- Real-time: show results when other side finishes ---
  useEffect(() => {
    if (!otherState.resultsVisible) return;
    setResultsVisible(true);
    if (Array.isArray(otherState.matchedPairs) && otherState.matchedPairs.length > 0) setMatchedPairs(otherState.matchedPairs);
    if (typeof otherState.wrongAttempts === 'number') setWrongAttempts(otherState.wrongAttempts);
    if (typeof otherState.firstTrySuccess === 'number') setFirstTrySuccess(otherState.firstTrySuccess);
  }, [otherState.resultsVisible]); // eslint-disable-line

  // --- Real-time: Try Again from other side ---
  useEffect(() => {
    const resetAt = otherState.resetAt;
    if (!resetAt || resetAt === prevResetAtRef.current) return;
    prevResetAtRef.current = resetAt;
    setSelectedWord(null);
    setMatchedPairs([]);
    setErrorPair(null);
    setFlashPair(null);
    setResultsVisible(false);
    setWrongAttempts(0);
    setFirstTrySuccess(0);
    attemptsRef.current = {};
    syncedRef.current = false;
    if (Array.isArray(otherState.shuffledTranslations)) setShuffledTranslations(otherState.shuffledTranslations);
  }, [otherState.resetAt]); // eslint-disable-line

  const getWordClass = (word) => {
    if (matchedPairs.some(p => p.word === word)) return 'wt-item matched';
    const classes = ['wt-item'];
    if (flashPair?.word === word || otherState.flashPair?.word === word) classes.push('correct');
    if (errorPair?.word === word || otherState.errorPair?.word === word) classes.push('incorrect');
    if (selectedWord === word) classes.push('selected');
    else if (otherState.selectedWord === word && selectedWord === null) classes.push('selected');
    return classes.join(' ');
  };

  const getTranslationClass = (translation) => {
    if (matchedPairs.some(p => p.translation === translation)) return 'wt-item matched';
    const classes = ['wt-item'];
    if (flashPair?.translation === translation || otherState.flashPair?.translation === translation) classes.push('correct');
    if (errorPair?.translation === translation || otherState.errorPair?.translation === translation) classes.push('incorrect');
    if (selectedWord) classes.push('selectable');
    return classes.join(' ');
  };

  const handleWordClick = (word) => {
    if (matchedPairs.some(p => p.word === word)) return;
    setSelectedWord(word);
    updateState({ selectedWord: word });
  };

  const handleTranslationClick = (translation) => {
    if (!selectedWord) return;
    if (matchedPairs.some(p => p.translation === translation)) return;

    const correct = pairs.find(p => p.word === selectedWord)?.translation === translation;
    const word = selectedWord;
    const isFirstTry = (attemptsRef.current[word] || 0) === 0;
    const capturedFirstTrySuccess = firstTrySuccess;
    const capturedWrongAttempts = wrongAttempts;

    if (correct) {
      const fp = { word, translation };
      setFlashPair(fp);
      setSelectedWord(null);
      updateState({ flashPair: fp, selectedWord: null });

      setTimeout(() => {
        const newFirstTrySuccess = capturedFirstTrySuccess + (isFirstTry ? 1 : 0);
        setMatchedPairs(prev => {
          const newMatchedPairs = [...prev, { word, translation }];
          const isAllMatched = newMatchedPairs.length === pairs.length;
          if (isAllMatched) {
            setResultsVisible(true);
            updateState({ matchedPairs: newMatchedPairs, resultsVisible: true, wrongAttempts: capturedWrongAttempts, firstTrySuccess: newFirstTrySuccess, flashPair: null });
          } else {
            updateState({ matchedPairs: newMatchedPairs, flashPair: null });
          }
          return newMatchedPairs;
        });
        if (isFirstTry) setFirstTrySuccess(newFirstTrySuccess);
        setFlashPair(null);
        attemptsRef.current[word] = (attemptsRef.current[word] || 0) + 1;
      }, 600);
    } else {
      const ep = { word, translation };
      setErrorPair(ep);
      attemptsRef.current[word] = (attemptsRef.current[word] || 0) + 1;
      setWrongAttempts(prev => prev + 1);
      setSelectedWord(null);
      updateState({ errorPair: ep, selectedWord: null });
      setTimeout(() => {
        setErrorPair(null);
        updateState({ errorPair: null });
      }, 700);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleCheck = () => setShowResult(true);

  const handleTryAgain = () => {
    const newShuffle = shuffle(pairs.map(p => p.translation));
    setShowResult(false);
    setSelectedWord(null);
    setMatchedPairs([]);
    setErrorPair(null);
    setFlashPair(null);
    setWrongAttempts(0);
    setFirstTrySuccess(0);
    attemptsRef.current = {};
    setResultsVisible(false);
    setShuffledTranslations(newShuffle);
    syncedRef.current = false;
    updateState({
      matchedPairs: [], resultsVisible: false, wrongAttempts: 0, firstTrySuccess: 0,
      selectedWord: null, flashPair: null, errorPair: null,
      shuffledTranslations: newShuffle, resetAt: Date.now()
    });
  };

  // eslint-disable-next-line no-unused-vars
  const handleUnmix = () => {
    setMode(mode === 'matching' ? 'glossary' : 'matching');
    setShowResult(false);
    setShuffledTranslations(shuffle(pairs.map(p => p.translation)));
  };

  // Results panel data — same ring/tier pattern as ABCD/Multiple Choice,
  // adapted to this exercise's own metrics: every word eventually gets
  // matched, so "score" here is first-try accuracy rather than
  // correct/incorrect out of a single attempt.
  let resultsData = null;
  if (resultsVisible) {
    const total = pairs.length || 1;
    const ratio = firstTrySuccess / total;
    const percent = Math.round(ratio * 100);
    const circumference = 2 * Math.PI * 42;
    const dashOffset = circumference * (1 - percent / 100);
    let tier = 'poor';
    let headline;
    let subline;
    if (ratio >= 0.9) { tier = 'excellent'; headline = 'Outstanding!'; subline = isStudent ? 'You nailed it.' : 'Student nailed it.'; }
    else if (ratio >= 0.7) { tier = 'great'; headline = 'Great job!'; subline = 'Solid performance.'; }
    else if (ratio >= 0.5) { tier = 'good'; headline = 'Good work.'; subline = isStudent ? 'Keep going — you’re close.' : 'Getting there.'; }
    else { tier = 'poor'; headline = 'Keep practicing'; subline = isStudent ? 'You’ll get it — try again.' : 'Review and try again.'; }
    resultsData = { percent, circumference, dashOffset, tier, headline, subline };
  }

  const handleEditKeyDown = (e) => {
    if (e.key === 'Escape') e.target.blur();
  };

  const maybeGenerateTranslation = async (index, wordValue) => {
    const word = String(wordValue || '').trim();
    if (!word) return;
    const current = pairs[index];
    if (!current || (current.translation && current.translation.trim().length > 0)) return;
    if (lastGeneratedRef.current[index] === word) return;

    try {
      setIsGenerating(prev => ({ ...prev, [index]: true }));
      const isPhrase = /\s/.test(word);
      const prompt = `You are a professional ${editableTargetLanguage} translator building a word-matching exercise, working to the quality of a serious bilingual learner's dictionary entry — not a quick machine-translation guess. The item "${word}" stands ALONE (not inside a sentence).
${isPhrase
  ? `This is a multi-word phrase — keep its own natural grammatical shape (a gerund phrase stays gerund-ish, a noun phrase stays a noun phrase) instead of flattening it to a bare infinitive. Example: "callously killing" → "безжалостно убивая" (keeps the gerund), not "убивать" (drops "callously" and changes the part of speech).
Tell apart two kinds of phrases: (a) COMPOSITIONAL phrases (meaning = its parts combined, e.g. "malicious gossip" = malicious + gossip) — translate each component faithfully and combine naturally; do NOT swap in a different, more "sophisticated" concept just because it's loosely related (e.g. "malicious gossip" → "злонамеренные сплетни"/"злостные слухи", staying on gossip/rumor — NOT "злословие, клевета", which means slander/defamation, a different concept). (b) TRUE IDIOMS (meaning isn't the sum of its parts, e.g. "to follow suit") — translate the meaning/function using the equivalent idiom.`
  : `This is a single word — give the BASE / CITATION FORM: verbs in the infinitive (or infinitive-equivalent citation form for ${editableTargetLanguage}), nouns in the singular nominative, adjectives in their neutral form. NEVER a conjugated, inflected, or person/tense-specific form (example: "can" the modal verb → "мочь", NOT "могу"/"может"). If it has multiple unrelated meanings, pick whichever is more common overall for a general-vocabulary word list.`}
Depth over terseness: if two or three closely-related senses or near-synonyms together capture the real meaning better than any single word could, give them joined with ", " (max 2-3, never a sprawling list, and every synonym must still translate the SAME word/phrase — richer must not mean "different concept") — that nuance is often what makes a translation feel professional instead of thin. But don't pad a simple, unambiguous item that only needs one word.
Translate the MEANING like a professional lexicographer for idioms, but stay faithful to the actual words for compositional phrases — avoid stiff "translationese" without drifting into a different concept. Use correct native spelling/script for ${editableTargetLanguage} (not a romanized transliteration, unless that language is conventionally written in Latin script). No parenthetical explanations or examples — only the translation(s) themselves, comma-joined if using more than one. Before answering, silently double-check the form, the sense, and whether the richness (one word vs. 2-3 synonyms) actually fits this item. Return ONLY the translation.`;
      const response = await fetchOpenAIText({ prompt, temperature: 0.3, maxTokens: 50 });
      if (response && response.trim()) {
        setPairs(prev => prev.map((p, i) => i === index ? { ...p, translation: response.trim() } : p));
        lastGeneratedRef.current[index] = word;
      }
    } catch (_) {
      // silent
    } finally {
      setIsGenerating(prev => ({ ...prev, [index]: false }));
    }
  };

  const handleAddPair = () => {
    setPairs(prev => [...prev, { word: '', translation: '', id: `pair_${Date.now()}` }]);
  };

  const handleDeletePair = (index) => {
    if (pairs.length <= 1) return;
    setPairs(prev => prev.filter((_, i) => i !== index));
  };

  const handleSave = () => {
    setIsEditing(false);
    if (typeof updateAttributes === 'function') {
      updateAttributes({ pairs, title: editableTitle, targetLanguage: editableTargetLanguage });
    }
    // Pairs may have changed (words/translations added, removed, or edited)
    // — shuffledTranslations only gets built once (on mount), so without
    // this it stays stale and newly-added translations never appear in
    // the play-mode Translations column. Reshuffle from the current pairs
    // and reset progress, same as "Try Again", since the underlying data
    // just changed.
    const newShuffle = shuffle(pairs.map(p => p.translation));
    setShuffledTranslations(newShuffle);
    setSelectedWord(null);
    setMatchedPairs([]);
    setErrorPair(null);
    setFlashPair(null);
    setWrongAttempts(0);
    setFirstTrySuccess(0);
    attemptsRef.current = {};
    setResultsVisible(false);
    syncedRef.current = false;
    updateState({
      matchedPairs: [], resultsVisible: false, wrongAttempts: 0, firstTrySuccess: 0,
      selectedWord: null, flashPair: null, errorPair: null,
      shuffledTranslations: newShuffle, resetAt: Date.now()
    });
  };

  return (
    <div className="wte-card">
      <div className="wt-header">
        <div className="wt-header-left">
          <div className="wt-header-icon">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 5h9M9 3v2M11.5 5c-.4 4-2.5 7.5-6 9.7M6 8.5c1.4 2.3 3.5 4 6 5.2"/>
              <path d="M14 21l4-9 4 9M15.4 18h5.2"/>
            </svg>
          </div>
          {isEditing ? (
            <input type="text" value={editableTitle} onChange={e => setEditableTitle(e.target.value)} className="wt-title-input" />
          ) : (
            <span className="wt-title">{editableTitle}</span>
          )}
          <span className="wt-language-badge">{editableTargetLanguage}</span>
        </div>
        <div className="wt-header-actions">
          {!isEditing && !isStudent && (
            <button className="wt-icon-btn" onClick={() => setIsEditing(true)} aria-label="Edit" data-tip="Edit">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="m18.5 2.5 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
            </button>
          )}
          {isEditing && (
            <>
              <button className="wt-icon-btn is-save" onClick={handleSave} aria-label="Save" data-tip="Save">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="9,12 11,14 15,10" />
                </svg>
              </button>
              <button className="wt-icon-btn is-cancel" onClick={() => { setIsEditing(false); setEditableTitle(title); setEditableTargetLanguage(targetLanguage); }} aria-label="Cancel" data-tip="Cancel">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </>
          )}
          {onClose && (
            <button className="wt-icon-btn" onClick={onClose} aria-label="Close" data-tip="Close">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="eye-icon">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" className="eye-open" />
                <circle cx="12" cy="12" r="3" className="eye-pupil" />
              </svg>
            </button>
          )}
        </div>
      </div>

      <div className="wt-body">
        {(mode === 'matching' && !isEditing) ? (
          <div className="wt-matching-section">
            {!resultsVisible ? (
              <>
                <div className="wt-matching-container">
                  <div className="wt-column">
                    <div className="wt-column-title">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20V2H6.5A2.5 2.5 0 0 0 4 4.5v15z"/>
                        <path d="M4 19.5A2.5 2.5 0 0 0 6.5 22H20v-3"/>
                      </svg>
                      Words
                    </div>
                    {pairs.map(({ word }, i) =>
                      matchedPairs.some(p => p.word === word) ? null : (
                        <div key={i} className={getWordClass(word)} data-word={word} onClick={() => handleWordClick(word)}>
                          {word}
                        </div>
                      )
                    )}
                  </div>
                  <div className="wt-column">
                    <div className="wt-column-title">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                        <path d="M4 5h9M9 3v2M11.5 5c-.4 4-2.5 7.5-6 9.7M6 8.5c1.4 2.3 3.5 4 6 5.2"/>
                        <path d="M14 21l4-9 4 9M15.4 18h5.2"/>
                      </svg>
                      Translations ({editableTargetLanguage})
                    </div>
                    {shuffledTranslations.map((translation, j) =>
                      matchedPairs.some(p => p.translation === translation) ? null : (
                        <div key={j} className={getTranslationClass(translation)} data-translation={translation} onClick={() => handleTranslationClick(translation)}>
                          {translation}
                        </div>
                      )
                    )}
                  </div>
                </div>

                {matchedPairs.length > 0 && (
                  <div className="wt-matched-list">
                    {matchedPairs.map((p, idx) => (
                      <div key={idx} className="wt-matched-row">
                        <span className="wt-matched-word">{p.word}</span>
                        <span className="wt-matched-sep">→</span>
                        <span className="wt-matched-translation">{p.translation}</span>
                      </div>
                    ))}
                  </div>
                )}

                {matchedPairs.length > 0 && (
                  <div className="wt-reset-row">
                    <button className="wt-reset-btn" onClick={handleTryAgain}>Start over</button>
                  </div>
                )}
              </>
            ) : (
              <>
                <div className={`wt-results tier-${resultsData.tier}`}>
                  <div className="wt-results-ring-wrap">
                    <svg viewBox="0 0 100 100" className="wt-results-ring" aria-hidden>
                      <circle cx="50" cy="50" r="42" className="wt-results-ring-track" />
                      <circle
                        cx="50"
                        cy="50"
                        r="42"
                        className="wt-results-ring-progress"
                        style={{ strokeDasharray: resultsData.circumference, strokeDashoffset: resultsData.dashOffset }}
                      />
                    </svg>
                    <div className="wt-results-ring-center">
                      <div className="wt-results-percent">{resultsData.percent}%</div>
                      <div className="wt-results-percent-label">first try</div>
                    </div>
                  </div>
                  <div className="wt-results-headline">{resultsData.headline}</div>
                  <div className="wt-results-subline">{resultsData.subline}</div>
                  <div className="wt-results-stats">
                    <div className="wt-results-stat is-correct">
                      <span className="wt-results-stat-icon" aria-hidden>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12"/>
                        </svg>
                      </span>
                      <span className="wt-results-stat-value">{firstTrySuccess}</span>
                      <span className="wt-results-stat-label">first-try</span>
                    </div>
                    <div className="wt-results-stats-divider" aria-hidden />
                    <div className="wt-results-stat is-incorrect">
                      <span className="wt-results-stat-icon" aria-hidden>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="18" y1="6" x2="6" y2="18"/>
                          <line x1="6" y1="6" x2="18" y2="18"/>
                        </svg>
                      </span>
                      <span className="wt-results-stat-value">{wrongAttempts}</span>
                      <span className="wt-results-stat-label">mistakes</span>
                    </div>
                  </div>
                  <button className="wt-results-cta" onClick={handleTryAgain}>
                    <span>Try Again</span>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                      <polyline points="23 4 23 10 17 10"/>
                      <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
                    </svg>
                  </button>
                </div>

                {(() => {
                  const list = matchedPairs.length > 0 ? matchedPairs : (Array.isArray(otherState.matchedPairs) ? otherState.matchedPairs : []);
                  return list.length > 0 ? (
                    <div className="wt-matched-list">
                      {list.map((p, idx) => (
                        <div key={idx} className="wt-matched-row">
                          <span className="wt-matched-word">{p.word}</span>
                          <span className="wt-matched-sep">→</span>
                          <span className="wt-matched-translation">{p.translation}</span>
                        </div>
                      ))}
                    </div>
                  ) : null;
                })()}
              </>
            )}
          </div>
        ) : (
          <div className="wt-glossary">
            <div className="wt-glossary-list">
              {pairs.map((pair, index) => (
                <div key={pair.id || index} className="wt-glossary-item">
                  <div className="wt-glossary-word">
                    {isEditing ? (
                      <input
                        type="text"
                        value={pair.word}
                        onChange={e => {
                          const value = e.target.value;
                          setPairs(prev => prev.map((p, idx) => idx === index ? { ...p, word: value } : p));
                          if (genTimersRef.current[index]) clearTimeout(genTimersRef.current[index]);
                          genTimersRef.current[index] = setTimeout(() => maybeGenerateTranslation(index, value), 700);
                        }}
                        onKeyDown={handleEditKeyDown}
                        placeholder="Enter word"
                        data-pair-index={index}
                        data-field="word"
                        className="wt-glossary-input"
                      />
                    ) : (
                      pair.word
                    )}
                  </div>
                  <div className="wt-glossary-translation">
                    {isEditing ? (
                      <span className="wt-translation-wrapper">
                        <input
                          type="text"
                          value={pair.translation}
                          onChange={e => setPairs(prev => prev.map((p, idx) => idx === index ? { ...p, translation: e.target.value } : p))}
                          onKeyDown={handleEditKeyDown}
                          placeholder="Enter translation"
                          data-pair-index={index}
                          data-field="translation"
                          className={`wt-glossary-input ${isGenerating[index] ? 'generating' : ''}`}
                        />
                        {isGenerating[index] && <span className="wt-translation-spinner" aria-label="Generating translation" />}
                      </span>
                    ) : (
                      pair.translation
                    )}
                  </div>
                  {isEditing && pairs.length > 1 && (
                    <button
                      className="wt-delete-btn"
                      onClick={e => { e.stopPropagation(); handleDeletePair(index); }}
                      title="Delete pair"
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
            </div>
            {isEditing && (
              <button ref={addBtnRef} className="wt-add-btn" onClick={handleAddPair}>
                + Add new pair
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default WordTranslationExercise;
